import { Map, Record, List } from 'immutable';
import { parameterize } from 'helpers/string';

import JobRecord from 'records/job_record';
import Address          from './address';

const struct = {
  id:                          undefined,
  title:                       undefined,
  positionTitle:               undefined,
  campaignType:                undefined,
  locale:                      undefined,
  brief:                       undefined,
  description:                 undefined,
  salary:                      undefined,
  salaryType:                  undefined,
  currencyType:                undefined,
  jobType:                     undefined,
  remoteType:                  undefined,
  createdAt:                   undefined,
  updatedAt:                   undefined,
  deletedAt:                   undefined,
  origin:                      new Map(),
  bearerToken:                 undefined,
  address:                     new Address(),
  facebookPixelId:             undefined,
  jobBanner:                   undefined,
  settings:                    {},
  companyJobBanner:            undefined,
  companyParameters:           {},
  company:                     new Map(),
  countryShortCode:            undefined,
  prompts:                     new List(),
  evaluationCriterias:         new List(),
  talkscoreWeightDistribution: new Map(),
  jobLandingPageUrl:           undefined,
  messengerUrl:                undefined,
  applyResume:                 undefined,
  timezone:                    undefined,
  applyUrl:                    undefined,
  onVonqOpn:                   undefined,
  recruitmentCenter:           undefined,
  payload:                     undefined,
  owner:                       undefined,

  isReceivingApplications: undefined,
  favorited:               undefined,
};

export default class CampaignRecord extends Record(struct, 'CampaignRecord') {
  cityLocation() {
    const address = new Address(this.address);

    return address.cityLocation();
  }

  remoteTypeTitle() {
    const remoteType = this.jobRemoteType();
    if (!remoteType) return null;

    return t(remoteType, { scope: 'eos.landing_page.text.remote_types', defaultValue: remoteType });
  }

  isLongForm() {
    return this.settings.landingPageType == 'single';
  }

  isOnboarding() {
    return this.campaignType === 'onboarding';
  }

  isJob() {
    return this.campaignType === 'job_application';
  }

  isActiveJob() {
    return this.isJob() && this.isReceivingApplications;
  }

  forceNewLandingPage() {
    return this.companyParameters.eosLandingPage === true;
  }

  hasFallbackBanner() {
    return this.forceNewLandingPage() && this.companyJobBanner !== undefined;
  }

  forceDefaultBanner() {
    return (
      this.forceNewLandingPage()
      && this.companyParameters.useBanner === true
      && this.companyJobBanner !== undefined
    );
  }

  companyLandingPageType() {
    if (!['single', 'multiple'].includes(this.companyParameters.landingPageType) || !this.forceNewLandingPage()) {
      return null;
    }

    return this.companyParameters.landingPageType;
  }

  jobTypeTitle() {
    const jtype = this.jobType || this.origin.jobType;
    return t(parameterize(jtype), { scope: 'eos.landing_page.text.job_type_options', defaultValue: t('eos.landing_page.text.job_type_options.default') });
  }

  salaryValue() {
    if (this.salary && `${this.salary}`.trim().length > 0) {
      const details = [this.salary, this.currencyType];

      if (this.salaryType) {
        const freq = t(this.salaryType, { scope: 'eos.landing_page.text.salary_types', defaultValue: this.salaryType });
        details.push(`/ ${freq}`);
      }

      return details.filter((i) => i).join(' ');
    }

    const job = new JobRecord(this.origin);

    return job.salaryDetails();
  }

  briefValue() {
    if (this.brief && this.brief.trim().length > 0) {
      return this.brief;
    }

    return this.origin.brief;
  }

  publicJobTitle() {
    const origin = new JobRecord(this.origin);

    if (origin.isTemplate() && this.positionTitle) {
      return this.positionTitle;
    }

    return origin.title;
  }

  jobCategory(locale = null) {
    if (!this.settings.jobCategory) {
      return null;
    }

    const value = t(this.settings.jobCategory, { scope: 'campaigns.edit.form.job_category.options', defaultValue: this.settings.jobCategory, lng: (locale || I18n.locale) });
    return value && value.length > 0 ? value : null;
  }

  jobIndustry(locale = null) {
    if (!this.settings.jobIndustry) {
      return null;
    }

    const value = t(this.settings.jobIndustry, { scope: 'campaigns.edit.form.job_industry.options', defaultValue: this.settings.jobIndustry, lng: (locale || I18n.locale) });
    return value && value.length > 0 ? value : null;
  }

  jobDescription() {
    return this.description || this.origin.description;
  }

  jobSalary() {
    return this.salary || this.origin.salary;
  }

  jobSalaryType() {
    return this.salaryType || this.origin.salaryType;
  }

  jobCurrencyType() {
    return this.currencyType || this.origin.currencyType;
  }

  jobTypeValue() {
    return this.jobType || this.origin.jobType;
  }

  jobRemoteType() {
    return this.remoteType || this.origin.remoteType;
  }
}
